import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useGetRaterTypesQuery } from '../../../services/SkillsManagement';
import LoaderSpinner from '../../../SystemDesign/LoaderSpinner';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { tokens } from '../../../theme';
import { SubTitleScreen, TitleScreen } from '../../../SystemDesign/Styles';
import { formattedDate, isEmpty } from '../../../Utils/Functions';
import DateInTable from '../../../SystemDesign/DateInTable';
import ListViewMenuActions from '../../../SystemDesign/ListViewMenuActions';
import ListViewV2 from '../../../SystemDesign/ListViewV2';
import useDebounce from '../../../Utils/useDebounce';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { AuthContext } from '../../../Context/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { INITIALIZE_RATER_TYPES, CLEAR_RATER_TYPES_LIST } from '../../../Reducers/Actions';

import AddRaterType from './AddRaterType';
import UpdateRaterType from './UpdateRaterType';
import DeleteRaterType from './DeleteRaterType';
import SeeMoreRater from './SeeMoreRater';

const RaterTypeList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMdOrLarger = useMediaQuery((theme) => theme.breakpoints.up('md'));

  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const { ability } = useContext(AuthContext);
  const dispatch = useDispatch();
  const reduxRaterTypes = useSelector((state) => state.raterTypes);

  const [columnFilters, setColumnFilters] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 600);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [queryParams, setQueryParams] = useState(() => {
    const savedRowsPerPage = localStorage.getItem('rater-types-rowsPerPage');
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    return {
      filters: '',
      sorting: '',
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage }),
    };
  });

  const { data, error, isLoading, isFetching } = useGetRaterTypesQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const handleSearch = (value) => setSearchValue(value);

  const actionsList = useMemo(() => [
    { component: SeeMoreRater, key: 'see-more' },
    ...(ability?.can('update', 'RaterType') ? [{ component: UpdateRaterType, key: 'update' }] : []),
    ...(ability?.can('delete', 'RaterType') ? [{ component: DeleteRaterType, key: 'delete' }] : []),
  ], [ability]);

  const columns = useMemo(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      enableColumnFilter: false,
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      cell: (info) => info.getValue() || '-',
      enableColumnFilter: false,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: 'Created at',
      cell: (info) => info.getValue() ? <DateInTable date={formattedDate(new Date(info.getValue()))} /> : '-',
      filterFn: 'between',
      type: 'date',
    },
    ...(isMdOrLarger ? [{
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: 'Updated at',
      cell: (info) => info.getValue() ? <DateInTable date={formattedDate(new Date(info.getValue()))} /> : '-',
      filterFn: 'between',
      type: 'date',
    }] : []),
    {
      id: 'actions',
      header: 'Actions',
      size: 100,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <ListViewMenuActions>
          {actionsList.map(({ component: Component, key }) => (
            <Component key={key} row={row} actionKey={key} />
          ))}
        </ListViewMenuActions>
      ),
    },
  ], [isMdOrLarger, actionsList]);

  const applyFilters = (
    updatedFilters = columnFilters,
    paginationInfo = null,
    sortingInfo = null,
    searchVal = null
  ) => {
    const filtersArray = updatedFilters || [];
    const formattedFilters = filtersArray.reduce((acc, filter) => {
      if (typeof filter === 'object' && !Array.isArray(filter)) {
        const [key, values] = Object.entries(filter)[0];
        if (Array.isArray(values)) {
          const nonEmpty = values.filter((v) => v !== '');
          if (nonEmpty.length > 0) acc[key] = nonEmpty.map(String);
        } else if (values !== '') {
          acc[key] = [String(values)];
        }
      }
      return acc;
    }, {});

    let rowsPerPage = paginationInfo?.rowsPerPage;
    if (!rowsPerPage) {
      const savedRowsPerPage = localStorage.getItem('rater-types-rowsPerPage');
      rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    }

    setQueryParams((prev) => ({
      ...prev,
      filters: isEmpty(formattedFilters) ? '' : JSON.stringify(formattedFilters),
      pagination: JSON.stringify({
        currentPage: paginationInfo?.currentPage || 1,
        rowsPerPage,
      }),
      sorting: sortingInfo ? JSON.stringify(sortingInfo) : prev.sorting,
      search: searchVal || '',
    }));
  };

  useEffect(() => {
    if (error) {
      setInfoAlert({ severity: 'error', message: error.data?.message || 'An error has occurred' });
      setOpenSnackBar(true);
    }
  }, [error]);

  useEffect(() => {
    if (data?.data?.raterTypes) {
      dispatch({ type: CLEAR_RATER_TYPES_LIST });
      dispatch({ type: INITIALIZE_RATER_TYPES, payload: data.data.raterTypes });
    }
  }, [data?.data?.raterTypes, dispatch]);

  return (
    <div>
      <Typography sx={TitleScreen(colors)}>Rater Types</Typography>
      <Typography sx={SubTitleScreen(colors)}>Rater Type management</Typography>
      {ability?.can('create', 'RaterType') && <AddRaterType />}

      <LoaderSpinner loading={isLoading} />
      <ListViewV2
        dispatchData={reduxRaterTypes}
        columns={columns}
        tableName="rater-types"
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        applyFilters={applyFilters}
        pagination={data?.data?.pagination}
        setQueryParams={setQueryParams}
        queryParams={queryParams}
        debouncedSearchValue={debouncedSearchValue}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
      />
    </div>
  );
};

export default RaterTypeList;
